import { Stack, StackItem, Typography } from "@octopus-energy/coral/atoms"
import { token } from "@octopus-energy/coral-design/tokens"
import { useTranslations } from "next-intl"
import { CIRCLE_SIZES_LOW_ZOOM } from "../constants/map"
import { css, cva } from "@octopus-energy/coral-design/css"

const MAX_CIRCLE_SIZE = CIRCLE_SIZES_LOW_ZOOM[CIRCLE_SIZES_LOW_ZOOM.length - 1]

const circleLegendItem = cva({
    base: {
        display: "flex",
        alignItems: "center",
        _before: {
            content: '""',
            display: "inline-block",
            width: "20px",
            height: "20px",
            borderRadius: "50%",
            marginRight: "8px",
            borderStyle: "solid",
        },
    },
    variants: {
        borderWidth: {
            thin: { _before: { borderWidth: 0.5 } },
            thick: { _before: { borderWidth: 3 } },
        },
        color: {
            solar: {
                _before: {
                    backgroundColor: token("colors.background.secondary.100"),
                    borderColor: token("colors.background.base.900"),
                },
            },
            wind: {
                _before: {
                    backgroundColor: token("colors.background.primary.500"),
                    borderColor: token("colors.background.base.900"),
                },
            },
        },
    },
    defaultVariants: {
        borderWidth: "thin",
    },
})

const CIRCLES_LEGEND_HEIGHT = 50

export function ProducersMapLegend() {
    const t = useTranslations("ProducersMapLegend")

    return (
        <Stack
            alignItems={{ md: "center" }}
            flexDirection={{ base: "column", md: "row" }}
            gap="xl"
        >
            <Stack flexDirection="column">
                <Stack flexDirection={{ base: "column", sm: "row" }}>
                    <Typography
                        color="base500"
                        textStyle="body2"
                    >
                        {t("label.octopus")}
                    </Typography>

                    <div className={circleLegendItem({ borderWidth: "thick", color: "wind" })}>
                        <Typography
                            color="base500"
                            textStyle="body2"
                        >
                            {t("value.wind")}
                        </Typography>
                    </div>

                    <div className={circleLegendItem({ borderWidth: "thick", color: "solar" })}>
                        <Typography
                            color="base500"
                            textStyle="body2"
                        >
                            {t("value.solar")}
                        </Typography>
                    </div>
                </Stack>

                <Stack flexDirection={{ base: "column", sm: "row" }}>
                    <Typography
                        color="base500"
                        textStyle="body2"
                    >
                        {t("label.partners")}
                    </Typography>

                    <div className={circleLegendItem({ color: "wind" })}>
                        <Typography
                            color="base500"
                            textStyle="body2"
                        >
                            {t("value.wind")}
                        </Typography>
                    </div>

                    <div className={circleLegendItem({ color: "solar" })}>
                        <Typography
                            color="base500"
                            textStyle="body2"
                        >
                            {t("value.solar")}
                        </Typography>
                    </div>
                </Stack>
            </Stack>

            <StackItem>
                <svg
                    className={css({
                        "& text": {
                            fontSize: "16px",
                            fill: token("colors.background.base.500"),
                            fontWeight: 400,
                        },

                        "& circle": {
                            fill: "none",
                            stroke: token("colors.background.base.500"),
                        },

                        "& line": {
                            stroke: token("colors.background.base.500"),
                            strokeOpacity: 0.5,
                        },
                    })}
                    width="300"
                    height={CIRCLES_LEGEND_HEIGHT}
                    viewBox={`0 0 300 ${CIRCLES_LEGEND_HEIGHT}`}
                    xmlns="http://www.w3.org/2000/svg"
                >
                    {CIRCLE_SIZES_LOW_ZOOM.filter((v) => v < 1000).map((size, index) => (
                        <circle
                            key={index}
                            cx={MAX_CIRCLE_SIZE + 2}
                            cy={CIRCLES_LEGEND_HEIGHT - size}
                            r={size}
                            stroke={token("colors.background.base.500")}
                        />
                    ))}
                    <line
                        x1={MAX_CIRCLE_SIZE}
                        y1={CIRCLES_LEGEND_HEIGHT - CIRCLE_SIZES_LOW_ZOOM[0] * 2}
                        x2={80}
                        y2={CIRCLES_LEGEND_HEIGHT - CIRCLE_SIZES_LOW_ZOOM[0] * 2}
                    />
                    <text
                        x={84}
                        y={CIRCLES_LEGEND_HEIGHT - CIRCLE_SIZES_LOW_ZOOM[0] * 2 + 5}
                    >
                        &lt; {CIRCLE_SIZES_LOW_ZOOM[1]}
                    </text>
                    <line
                        x1={MAX_CIRCLE_SIZE}
                        y1={CIRCLES_LEGEND_HEIGHT - MAX_CIRCLE_SIZE * 2}
                        x2={80}
                        y2={CIRCLES_LEGEND_HEIGHT - MAX_CIRCLE_SIZE * 2}
                    />
                    <text
                        x={84}
                        y={CIRCLES_LEGEND_HEIGHT - MAX_CIRCLE_SIZE * 2 + 5}
                    >
                        &gt; {CIRCLE_SIZES_LOW_ZOOM[CIRCLE_SIZES_LOW_ZOOM.length - 2]} foyers
                        fournis
                    </text>
                </svg>
            </StackItem>
        </Stack>
    )
}

ProducersMapLegend.messages = ["ProducersMapLegend"] as const
